import type {IndexStrategy} from "../types/index-strategy.interface";

/**
 * StandardIndexStrategy - key-to-index mapping
 *
 * Keeps a bijection (in practice: unique mapping) between a stable key and an index.
 * Does NOT store the actual axis values; Axis is expected to keep index -> TValue separately.
 *
 * Index 0 = Origin (Reference Point) - set via constructor, immutable.
 *
 * @template TKey - Stable key type used for indexing (string | number | symbol)
 */
export class StandardIndexStrategy<TKey extends PropertyKey> implements IndexStrategy<TKey> {

    private readonly keyToIndex = new Map<TKey, number>();

    /**
     * Reverse-map for O(1) stale-key cleanup.
     *
     * Enforces bijection invariant: 1 key <-> 1 index.
     * When a new key occupies an existing index, the previous key is automatically removed from keyToIndex.
     *
     * Memory tradeoff: ~16 KB additional for typical 1000-entry axis (negligible for O(1) cleanup).
     */
    private readonly indexToKey = new Map<number, TKey>();

    private minIndex: number;
    private maxIndex: number;

    /**
     * Create a new StandardIndexStrategy with origin key at index 0
     *
     * @param originKey - The origin key, will be stored at index 0
     */
    constructor(private readonly originKey: TKey) {
        this.keyToIndex.set(originKey, 0);
        this.indexToKey.set(0, originKey);
        this.minIndex = 0;
        this.maxIndex = 0;
    }

    /**
     * Determines if negative growth is allowed.
     *
     * @return {boolean} True if negative growth is allowed, otherwise false.
     */
    canGrowNegative(): boolean {
        return true;
    }

    /**
     * Retrieves the origin key associated with this instance.
     *
     * @return {TKey} The origin key.
     */
    getOriginKey(): TKey {
        return this.originKey;
    }

    /**
     * Retrieves the index associated with the specified key.
     *
     * @param {TKey} key - The key whose associated index is to be retrieved.
     * @return {number | undefined} The index associated with the key, or undefined if the key does not exist.
     */
    getIndex(key: TKey): number | undefined {
        return this.keyToIndex.get(key);
    }

    /**
     * Inserts items into the key-to-index mapping, updating the minimum and maximum index values as needed.
     * Throws an error if a key already exists with a different index.
     *
     * Automatically cleans up stale keys: When a new key occupies an existing index,
     * the previous key at that index is removed from keyToIndex (O(1) via indexToKey reverse-map).
     *
     * @param {Array<{ key: TKey; index: number }>} items - An array of objects containing a key and an index to insert into the mapping.
     * @return {void} No return value.
     */
    insertAt(items: Array<{ key: TKey; index: number }>): void {
        for (const { key, index } of items) {
            const existingIndex = this.keyToIndex.get(key);
            if (existingIndex !== undefined && existingIndex !== index) {
                throw new Error(`Key already exists with different index: ${existingIndex} vs ${index}`);
            }

            // O(1) stale-key cleanup via reverse-map
            const previousKey = this.indexToKey.get(index);
            if (previousKey !== undefined && previousKey !== key) {
                this.keyToIndex.delete(previousKey);
            }

            this.keyToIndex.set(key, index);
            this.indexToKey.set(index, key);

            if (index < this.minIndex) this.minIndex = index;
            if (index > this.maxIndex) this.maxIndex = index;
        }
    }

    getIndexSpanByKey(startKey: TKey, endKey: TKey): number[] {
        const startIdx = this.keyToIndex.get(startKey);
        const endIdx = this.keyToIndex.get(endKey);

        if (startIdx === undefined) {
            throw new Error(`Start key not found in index cache: ${String(startKey)}`);
        }
        if (endIdx === undefined) {
            throw new Error(`End key not found in index cache: ${String(endKey)}`);
        }
        if (endIdx < startIdx) {
            throw new Error(`End index is before start index: ${startIdx} -> ${endIdx}`);
        }

        const length = endIdx - startIdx + 1;
        return Array.from({ length }, (_, i) => startIdx + i);
    }

    getMinIndex(): number {
        return this.minIndex;
    }

    getMaxIndex(): number {
        return this.maxIndex;
    }

    size(): number {
        return this.keyToIndex.size;
    }
}